import React, { useEffect, useState } from 'react';
import { Container, Card, CardContent, Typography, Box, CircularProgress, LinearProgress, Button } from '@mui/material';
import Grid from "@mui/material/Grid2";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/authService';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ProductsTable from '../../components/amazon/ProductsTable';
import WalmartProductsTable from '../../components/walmart/WalmartProductsTable';
import FilterBar from '../../components/amazon/FilterBar';
import WalmartFilterBar from '../../components/walmart/WalmartFilterBar';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import ScansTable from './ScansTable';

// Create a vintage theme
const vintageTheme = createTheme({
  palette: {
    primary: {
      main: '#7e3e1b',
    },
    secondary: {
      main: '#d2a679',
    },
    background: {
      default: '#f5e6d3',
      paper: '#efe1c6',
    },
    text: {
      primary: '#4a3728',
      secondary: '#7e3e1b',
    },
  },
  typography: {
    fontFamily: 'RobotoSlab',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 8px rgba(126, 62, 27, 0.2)',
          border: '1px solid #7e3e1b',
          borderRadius: '10px',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0 5px',
          // border: '2px solid #7e3e1b',
          borderRadius: '5px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
        head: {
          fontWeight: 'bold',
          color: '#7e3e1b',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:first-of-type': {
            '& > th': {
              borderBottom: '2px solid #7e3e1b',
              fontSize: 'large',
            },
          },
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(126, 62, 27, 0.05)',
          },
        },
      },
    },
  },
});

const Dashboard = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [scanData, setScanData] = useState([]);
  const [favoritesData, setFavoritesData] = useState([]);
  const [favorites, setFavorites] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortASC, setSortASC] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const user = getUser();
        const tkk = localStorage.getItem('tkk');

        const result = await fetch(`https://server.nepeto.com/get_aniseller_user/${user}/${tkk}/`);
        const resData = await result.json();
        if (resData.ok) {
          setScanData(resData.data.recent_scans);
          let categoriesTemp = [];
          resData.data.favorites.forEach((product) => {
            const categoryName = product.category;
            const store = product?.asin === undefined ? "walmart" : "amazon";

            if (!categoriesTemp.find((item) => item.name === categoryName)) {
              categoriesTemp.push({ name: categoryName, store });
            }
          });

          setCategories(categoriesTemp)

          const uniqueProducts = new Map();
          resData.data.favorites.forEach((product) => {
            const productId = product?.asin || product?.item_id;
            uniqueProducts.set(productId, product);
          });
          // Convert back to an array of unique products
          const uniqueProductsArray = Array.from(uniqueProducts.values());

          setFavoritesData(uniqueProductsArray);
        }
      }
      catch {

      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const filterData = () => {
    let tempData = [];
    favoritesData.forEach((product) => {
      if (product.category === category.name) {
        tempData.push(product);
      }
    })
    return tempData;
  }

  if (!props.isAuth) {
    navigate('/login');
  }

  return (
    <ThemeProvider theme={vintageTheme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 1, p: 3, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom align="center" color="primary" sx={{ mb: 4 }}>
          {favorites ? 'Favorites' : 'Dashboard'}
        </Typography>
        <Button onClick={() => setFavorites(!favorites)} variant="contained" color="primary" style={{margin: "auto", display: "block"}}>
          <Typography style={{ fontFamily: 'RobotoSlab', display: 'flex', alignItems: 'center', margin: 'auto', cursor: 'pointer' }}>
            {favorites ? <SpaceDashboardIcon /> : <FavoriteBorderIcon />}&nbsp;<strong style={{ fontSize: "120%" }}>{favorites ? ' Show Dashboard' : ' Show Favorites'}</strong>
          </Typography>
        </Button>
      </Container>

      {loading ? (
        <div style={{ color: '#7e3e1b', }}>
          <LinearProgress sx={{ marginTop: '16px' }} color={'inherit'} />
        </div>
      ) : (
        <>
          {favorites ? (
            <>
              <Typography variant="h5" align="center" color="#7e3e1b" gutterBottom>
              {category !== "" && <Button variant="contained" color="primary" onClick={() => setCategory("")}>Go Back</Button>}&emsp;Search by Category
              </Typography>

              {category === "" ? (
                <Container sx={{ mt: 4 }}>
                  <Grid container spacing={4}>
                    {["amazon", "walmart"].map((store) => (
                      <Grid item key={store} size="grow">
                        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}>
                          {store.charAt(0).toUpperCase() + store.slice(1)} Categories
                        </Typography>
                        <Grid container spacing={2} display="flex" justifyContent="center">
                          {categories
                            .filter((category) => category.store === store)
                            .map((category, index) => (
                              <Grid item xs={6} key={index}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => setCategory(category)}
                                  sx={{
                                    backgroundColor: store === "amazon" ? "#ff9900" : "#0071dc",
                                    color: "white",
                                    "&:hover": {
                                      backgroundColor: store === "amazon" ? "#e68a00" : "#005bb5",
                                    },
                                  }}
                                >
                                  {category.name}
                                </Button>
                              </Grid>
                            ))}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Container>
              ) : (<>
                {category.store === "walmart" ? <>
                  <WalmartFilterBar
                    data={filterData()}
                    setFilters={setFilters}
                    filters={filters}
                    activePlanLevel={"Master Plan"}
                  />
                  <WalmartProductsTable
                    data={filterData()}
                    setData={() => alert('setData')}
                    isMobile={false}
                    supplierType={"storefront"}
                    hideCredits={0}
                    showAdv={true}
                    isProductsLoading={false}
                    filters={filters}
                    setFilters={setFilters}
                    sortASC={sortASC}
                    setSortASC={setSortASC}
                    identifier={'Barcode'}
                    sellerId={''}
                    refreshToken={''}
                  />
                </> : <>
                  <FilterBar
                    data={filterData()}
                    setFilters={setFilters}
                    filters={filters}
                    activePlanLevel={"Master Plan"}
                  />

                  <ProductsTable
                    data={filterData()}
                    setData={() => alert('setData')}
                    isMobile={false}
                    supplierType={"storefront"}
                    hideCredits={0}
                    showAdv={true}
                    isProductsLoading={false}
                    filters={filters}
                    setFilters={setFilters}
                    sortASC={sortASC}
                    setSortASC={setSortASC}
                    identifier={'Barcode'}
                    sellerId={''}
                    refreshToken={''}
                  />
                </>}
              </>)}
            </>
          ) : (
            <Container>
              <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
                <Grid item >
                  <Card elevation={0}>
                    <CardContent>
                      {loading ? (
                        <Box style={{ display: 'flex', justifyContent: 'center', }}>
                          <CircularProgress style={{ color: '#7e3e1b' }} />
                        </Box>
                      ) : (
                        <Typography variant="h4" color="primary">{scanData ? scanData.reduce((total, item) => total + item.lineCount, 0) : 0}</Typography>
                      )}
                      <Typography variant="h6" component="div" color="secondary">
                        Total Items Scanned
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item>
                  <Card elevation={0}>
                    <CardContent>
                      {loading ? (
                        <Box style={{ display: 'flex', justifyContent: 'center', }}>
                          <CircularProgress style={{ color: '#7e3e1b' }} />
                        </Box>
                      ) : (
                        <Typography variant="h4" color="primary">{scanData ? scanData.length : 0}</Typography>
                      )}
                      <Typography variant="h6" component="div" color="secondary">
                        Files Uploaded
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item>
                  <Card elevation={0}>
                    <CardContent>
                      {loading ? (
                        <Box style={{ display: 'flex', justifyContent: 'center', }}>
                          <CircularProgress style={{ color: '#7e3e1b' }} />
                        </Box>
                      ) : (
                        <Typography variant="h4" color="primary">{scanData ? scanData.filter(scan => scan?.store === "amazon").reduce((total, item) => total + item.lineCount, 0) : 0}</Typography>
                      )}
                      <Typography variant="h6" component="div" color="secondary">
                        Amazon Items Scanned
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item>
                  <Card elevation={0}>
                    <CardContent>
                      {loading ? (
                        <Box style={{ display: 'flex', justifyContent: 'center', }}>
                          <CircularProgress style={{ color: '#7e3e1b' }} />
                        </Box>
                      ) : (
                        <Typography variant="h4" color="primary">{scanData ? scanData.filter(scan => scan?.store === "walmart").reduce((total, item) => total + item.lineCount, 0) : 0}</Typography>
                      )}
                      <Typography variant="h6" component="div" color="secondary">
                        Walmart Items Scanned
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <ScansTable scanData={scanData} />
            </Container>
          )}
        </>
      )}
    </ThemeProvider>
  );
};

export default Dashboard;